import { memo, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Routes as ReactRoutes, Route, useLocation } from 'react-router-dom'

import { PrivateRoute } from 'core-components/sitebuilder/routes/PrivateRoute'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { KeyboardController } from 'core-components/wrappers/keyboard'
import { withTracker } from 'src/core/analytics/withTracker'
import { applicationUpdating } from 'src/core/classes/services/InitializationServiceCore'
import LayoutClean from 'src/core/layout/LayoutClean'
import { SiteUpdateLayout } from 'src/core/layout/SiteUpdateLayout'
import { IRouteData } from 'src/core/routes/types'
import { URLController } from 'src/core/routes/urlController'

import { Page404 } from './404'
import { DEFAULT_CUSTOM_ROUTES } from './constants/defaultCustomRoutes'

export interface IRouteProps {
  CustomRoutes?: IRouteData[]
}

export const Routes = memo(({ CustomRoutes }: IRouteProps) => {
  const [privateRoutes] = useState({})
  const user = useSelector(selectUser)
  const isLoggedIn = !!user.id
  const location = useLocation()

  const routes = useMemo(() => {
    if (applicationUpdating) {
      return <Route path={'/*'} element={<SiteUpdateLayout />} />
    }

    const routesData = [...DEFAULT_CUSTOM_ROUTES, ...(CustomRoutes || [])]

    if (!isLoggedIn) {
      const getGuestRoutes = () => {
        const routes = routesData.filter((router) => {
          return router.access === 'Guest' || router.access === 'Common'
        })
        const baseRoute = routes.find((routeData) => routeData.path === '/')
        const loginRoute = routes.find((routeData) => routeData.path === '/login')
        const to = baseRoute?.path || loginRoute?.path || routes[0]?.path

        routes.push({
          path: '*',
          element: <Navigate to={to} replace state={{ from: location.pathname }} />,
        })

        return routes.map((item: IRouteData) => {
          return <Route path={item.path} element={item.element} key={item.path} />
        })
      }

      return getGuestRoutes()
    }

    const getPrivateRoute = (path: string) => {
      const cachePath = path.endsWith('/*') ? path.slice(0, -2) : path

      if (!privateRoutes[cachePath]) {
        privateRoutes[cachePath] = <PrivateRoute tracker={withTracker} EmptyLayout={LayoutClean} />
      }

      return <Route path={path} key={path} element={privateRoutes[cachePath]} />
    }

    const getAuthorizedRoutes = () => {
      const routes = routesData.filter((router) => {
        return (
          router.access === 'Authorized' ||
          router.access === 'Common' ||
          router.access === undefined
        )
      })

      return routes.map((item: IRouteData) => {
        if (item.wrappedInLayout) {
          return getPrivateRoute(item.path)
        }
        return <Route path={item.path} element={item.element} key={item.path} />
      })
    }

    return (
      <>
        {getAuthorizedRoutes()}
        {getPrivateRoute('/')}
        {getPrivateRoute('/page/:pageId')}
        {getPrivateRoute('/page/:pageId/*')}
        <Route path={'*'} element={<Page404 />} />
      </>
    )
  }, [isLoggedIn, CustomRoutes, privateRoutes])

  return (
    <KeyboardController>
      <URLController>
        <ReactRoutes>{routes}</ReactRoutes>
      </URLController>
    </KeyboardController>
  )
})

Routes.displayName = 'Routes'
